import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import AppSwitcherIcon from "@atlaskit/icon/glyph/app-switcher";
import CrossIcon from "@atlaskit/icon/glyph/cross";
import storage from "../../../utils/storage";
import Environment from "../../../common/environment";
import { ProductKeys } from "../../../common/hamlet/hamlet-user";
import imkt from "../../../imkt";
import { trackEvent } from "../../../common/analytics";

// ==================================================================
// Style divs
// ==================================================================
const SigninPromptStyledContainer = styled.div`
    display: flex;
    position: relative;
    justify-content: center;
    height: 100%;

    .signin-prompt-dropdown-menu-wrapper {
        position: absolute;
        top: 100%;
        padding-top: 10px;

        &.hide-dropdown {
            visibility: hidden;
        }
        
        @media (max-width: 1320px) {
            right: -50px;
        }
        
        &.menu-stuck {
            position: fixed;
            top: 0;
            z-index: 99999;
        }
    }
`;

const SigninPromptIconStyledDiv = styled.button`
    display: flex;
    border: none;
    background: transparent;
    justify-content: center;
    cursor: pointer;

    &.text-label-parent {
        display: flex;
        align-items: center;
        &:before {
            content: "";
            font-size: 0.875rem;
            line-height: 2.15;
        };

        > span {
            border-radius: 50%;
            padding: 4px;
            height: 36px;
            width: 36px;

            &:hover,
            &:focus {
                svg {
                    color: #1868DB;
                }
            }
        };
    };
    &.isHoveredInEffect {
        &.text-label-parent {
            > span {
                background: #e9f2fe;
                svg {
                    color: #1868DB;
                }
            };
        }
    }
`;

const SigninPromptDropdownMenuStyledDiv = styled.div`
    display: block;
    inset-block-start: 3rem;
    border-radius: 0.375rem;
    background-color: #ffffff;
    width: 340px;
    box-shadow: rgba(0, 0, 0, 0.2) 0px 8px 16px 0px;
    padding: 0;
    z-index: 19;

    .signin-prompt-header-container {
        padding: 24px 16px 12px 24px;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        gap: 8px;
        cursor: default;

        .signin-prompt-header {
            overflow-warp: break-word;
            text-align: left;
            display: block;
            font-weight: 500;
            line-height: 125%;

            .signin-prompt-header-text {
                font-size: 1.25rem;
                color: #101214;
                margin: 0;
                padding-bottom: 16px;
                font-weight: 600;
                line-height: 130%;
            };
            
            .signin-prompt-span-text {
                font-size: 12px;
                font-style: normal;
                width: 100%;
                text-transform: uppercase;
                padding: 0;
                color: #44546F;
            }
        }

        .closed-icon-container {
            display: block;
            inset-inline-end: 1.5rem;
            inset-block-start: 2rem;
            button.close-icon {
                cursor: pointer;
                display: flex;
                justify-content: center;
                align-items: center;
                height: 24px;
                width: 24px;
                background: none;
                border: none;
                float: inline-end;
                flex-direction: column;
                position: relative;
            };
        };
    }

    .signin-prompt-panels-container {
        align-items: center;
        width: 100%;
        border-radius: 4px;
        padding: 0 16px 24px 16px;
        position: relative;

        .signin-prompt-panel-link {
            text-decoration: none;
            color: #101214;
            display: block;
            width: 100%;
            cursor: pointer;
            border-radius: 0.25rem;
            transition: background-color 0.2s ease;
            &:hover {
                background-color: #F0F1F2;
            };

            .signin-prompt-panel {
                align-items: center;
                display: flex;
                flex-direction: row;
                gap: 8px;
                padding: 8px;
                position: relative;

                .signin-prompt-panel-logo {
                    width: 32px;
                    height: 32px;
                }

                .signin-prompt-panel-header {
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 20px;
                    letter-spacing: 0;
                };
            };
        };
    }
});
`;

const pulseKeyframes = keyframes`
    0% {
        transform: scale(0.01);
        opacity: 0.5;
    }
    30% {
        transform: scale(1.5);
        opacity: 0.5;
    }
    40% {
        transform: scale(1.5);
        opacity: 0.5;
    }
    70% {
        transform: scale(0.01);
        opacity: 0.5;
    }
    100% {
        transform: scale(0.01);
        opacity: 0.5;
    }
`;

const PulsatingCircleStyle = styled.div`
    position: absolute;
    inset-block-start: 50%;
    inset-inline-start: 50%;
    width: 36px;
    height: 36px;
    margin-block-start: -18px;
    margin-inline-start: -18px;
    border-radius: 50%;
    background: #e9f2fe;
    mix-blend-mode: multiply;
    animation: ${pulseKeyframes} 1.5s ease-in-out infinite;
    z-index: -1;
    transform-origin: center center;
`;

// ==================================================================
// End of Style divs
// ==================================================================

// ==================================================================
// Component types
// ==================================================================
type SigninPromptIconProps = {
    isHovered: boolean;
    isShowPulsingButton: boolean;
    isOpenDropdown: boolean;
    setIsOpenDropdown: (param: boolean) => void;
    setIsHovered: (param: boolean) => void;
    setIsShowPulsingButton: (param: boolean) => void;
};

type SigninPromptDropdownMenuProps = {
    welcomeBackText: string;
    pickupWhereYouLeftOffText: string;
    lastVisitedAppsText: string;
    lastVisitedAppsTextSignular: string;
    setIsOpenDropdown: (param: boolean) => void;
    displayProducts?: siteGrowthUnifiedProfileGetUnifiedProfileSitesProductType[]
    setIsShowPulsingButton: (param: boolean) => void;
    setIsHovered: (param: boolean) => void;
};

type siteGrowthUnifiedProfileGetUnifiedProfileSiteType = {
    cloudId: string | null | undefined;
    displayName: string | null | undefined;
    siteCreatedAt: string | null | undefined;
    url: string | null | undefined;
    hasAdminAccess: boolean | null | undefined;
    products: siteGrowthUnifiedProfileGetUnifiedProfileSitesProductType[];
};

type siteGrowthUnifiedProfileGetUnifiedProfileSitesProductType = {
    productName: string;
    productKey: string;
    productEdition: string | null | undefined;
    d0Eligible: boolean;
    d1to6Eligible: boolean;
    d0Active: boolean;
    d1to6Active: boolean;
    provisionedAt: string | null | undefined;
};

// ==================================================================
// End of Component types
// ==================================================================

// ==================================================================
// Utility functions
// ==================================================================

const targetedProductKeys = [
    ProductKeys.JIRA_SOFTWARE,
    ProductKeys.CONFLUENCE,
    ProductKeys.JIRA_SERVICE_MANAGEMENT
];

const sortByd1to6inActiveAndProvisionedAtTime = (
    a: siteGrowthUnifiedProfileGetUnifiedProfileSitesProductType, 
    b: siteGrowthUnifiedProfileGetUnifiedProfileSitesProductType
) => {
    const d1to6ActiveComparison = Number(a.d1to6Active) - Number(b.d1to6Active);
    if (d1to6ActiveComparison !== 0) {
        return d1to6ActiveComparison;
    }

    // If both have the same d1to6Active value, sort by provisionedAt, set most recent first
    const dateA = a.provisionedAt ? new Date(a.provisionedAt).getTime() : 0;
    const dateB = b.provisionedAt ? new Date(b.provisionedAt).getTime() : 0;
    return dateB - dateA;
}

const groupAndSortProducts = (gupsData: siteGrowthUnifiedProfileGetUnifiedProfileSiteType[]) => {
    const displayProductsCategories = {
        [ProductKeys.JIRA_SOFTWARE]: [] as Array<siteGrowthUnifiedProfileGetUnifiedProfileSitesProductType>,
        [ProductKeys.CONFLUENCE]: [] as Array<siteGrowthUnifiedProfileGetUnifiedProfileSitesProductType>,
        [ProductKeys.JIRA_SERVICE_MANAGEMENT]: [] as Array<siteGrowthUnifiedProfileGetUnifiedProfileSitesProductType>,
    };

    // Group products by productKey and get the first eligible product for each category
    gupsData.forEach(site => {
        site.products.forEach(product => {
            if (targetedProductKeys.includes(product.productKey) && product.d1to6Eligible) {
                displayProductsCategories[product.productKey].push(product);
            }
        });
    });

    // Sort products for each category based on d1to6Active d1to6Active false first, true later
    for (const [key, products] of Object.entries(displayProductsCategories)) {
        displayProductsCategories[key] = products.sort((a, b) => sortByd1to6inActiveAndProvisionedAtTime(a, b));
    }

    // Get the first product in each category and removed undefined values
    // Sort list again based on d1to6Active status where d1to6Active false first, true later
    return Object.values(displayProductsCategories)
        .map(products => products[0])
        .filter(Boolean)
        .sort((a, b) => sortByd1to6inActiveAndProvisionedAtTime(a, b)); 
};

const onGraphqlApiRequest = async (query: string): Promise<Response> => {
    const graphqlUrl = `${Environment.getAPIPrivateURL()}/graphql`;
    return fetch(graphqlUrl, {
        method: "POST",
        credentials: "include",
        headers: {
            "Content-type": "application/json",
            "x-use-case": "signin-prompt-logged-out"
        },
        body: JSON.stringify({ query: query })
    });
};

const getUserProductsViaGUPS = async (anonymousId: string) => {
    const cleanAnonymousId = decodeURIComponent(anonymousId).replace(/['"]+/g, "");
    const query = ` 
        query getProfile {
          growthUnifiedProfile_getUnifiedProfile(
            anonymousId: "${cleanAnonymousId}"
          ) {
            sites {
              cloudId
              displayName
              siteCreatedAt
              url
              hasAdminAccess
              products {
                productName
                productKey
                productEdition
                d0Eligible
                d1to6Eligible
                d0Active
                d1to6Active
                provisionedAt
              }
            } 
          }
        }
    `;

    const response = await onGraphqlApiRequest(query);
    if (response.ok) {
        const responseJson = await response.json();
        const siteProductList = responseJson
            .data
            .growthUnifiedProfile_getUnifiedProfile
            .sites as siteGrowthUnifiedProfileGetUnifiedProfileSiteType[];
        if (siteProductList.length === 0) {
            return [];
        }
        const groupAndSortedProductCategories = groupAndSortProducts(siteProductList);
        return groupAndSortedProductCategories;
    }
    throw 'Unable to fetch user products';
}

// ==================================================================
// End of Utility functions
// ==================================================================

// ==================================================================
// Sub components
// ==================================================================

//  "mixBlendMode": "multiply",
const SigninPromptIcon = ({
    isHovered,
    isShowPulsingButton,
    isOpenDropdown,
    setIsOpenDropdown,
    setIsHovered,
    setIsShowPulsingButton
}: SigninPromptIconProps) => {
    return (
        <SigninPromptIconStyledDiv
            tabIndex={0}
            role="button"
            className={`text-label-parent ${isHovered ? "isHoveredInEffect" : ""}`}
            onClick={() => {
                setIsHovered(!isHovered);
                setIsOpenDropdown(!isOpenDropdown);
                setIsShowPulsingButton(false);
                trackEvent("linkButton clicked", {
                    eventComponent: "linkButton",
                    action: "clicked",
                    component: "signinPromptIcon",
                    source: "globalNav",
                    actionSubjectId: "SigninPromptIcon",
                  });
            }}
            onKeyDown={(e) => {
                if (e.code == "Enter" || e.code == "Space") {
                    e.preventDefault();
                    setIsHovered(!isHovered);
                    setIsOpenDropdown(!isOpenDropdown);
                    setIsShowPulsingButton(false);
                    trackEvent("linkButton clicked", {
                        eventComponent: "linkButton",
                        action: "clicked",
                        component: "signinPromptIcon",
                        source: "globalNav",
                        actionSubjectId: "SigninPromptIcon",
                      });
                }
            }}
            data-testid="global-nav-signin-prompt-icon"
        >
           {isShowPulsingButton && <PulsatingCircleStyle />}
            <AppSwitcherIcon
                size="large"
                primaryColor={"#101214"}
                label="Signin Prompt Icon"
            />
        </SigninPromptIconStyledDiv>
    );
};

const JiraLogo = () => {
    return (
        <svg height="100%" viewBox="0 0 24 24">
            <path fill="var(--tile-color,#1868db)" d="M0 6a6 6 0 0 1 6-6h12a6 6 0 0 1 6 6v12a6 6 0 0 1-6 6H6a6 6 0 0 1-6-6z"></path>
            <path fill="var(--icon-color, white)" d="M8.974 15.524H7.62c-2.04 0-3.502-1.25-3.502-3.079h7.271c.377 0 .62.268.62.647v7.317c-1.817 0-3.036-1.472-3.036-3.524zm3.591-3.636h-1.352c-2.04 0-3.503-1.227-3.503-3.056h7.272c.376 0 .642.245.642.624v7.317c-1.818 0-3.059-1.472-3.059-3.524zm3.614-3.614h-1.353c-2.04 0-3.502-1.25-3.502-3.079h7.271c.377 0 .62.268.62.625v7.317c-1.817 0-3.036-1.472-3.036-3.525z"></path>
        </svg>
    );
};

const ConfluenceLogo = () => {
    return (
        <svg height="100%" viewBox="0 0 24 24">
            <path fill="var(--tile-color,#1868db)" d="M0 6a6 6 0 0 1 6-6h12a6 6 0 0 1 6 6v12a6 6 0 0 1-6 6H6a6 6 0 0 1-6-6z"></path>
            <path fill="var(--icon-color, white)" d="M17.495 14.265c-3.704-1.79-4.785-2.058-6.346-2.058-1.83 0-3.391.761-4.785 2.902l-.229.35c-.187.288-.229.39-.229.514s.063.227.292.37l2.35 1.462c.125.083.23.124.333.124.125 0 .208-.062.333-.247l.375-.577c.582-.885 1.102-1.173 1.768-1.173.583 0 1.27.165 2.122.577l2.455 1.152c.25.124.52.062.645-.226l1.165-2.552c.125-.289.042-.474-.25-.618M6.697 9.757c3.703 1.791 4.785 2.059 6.346 2.059 1.83 0 3.39-.762 4.785-2.902l.229-.35c.187-.288.228-.391.228-.515 0-.123-.062-.226-.29-.37l-2.352-1.462c-.125-.082-.229-.123-.333-.123-.125 0-.208.061-.333.247l-.374.576c-.583.885-1.103 1.173-1.769 1.173-.582 0-1.269-.164-2.122-.576L8.257 6.361c-.25-.123-.52-.061-.645.227L6.447 9.14c-.125.288-.041.473.25.617"></path>
        </svg>
    );
};

const JSMLogo = () => {
    return (
        <svg height="100%" viewBox="0 0 24 24">
            <path fill="var(--tile-color,#ffc716)" d="M0 6a6 6 0 0 1 6-6h12a6 6 0 0 1 6 6v12a6 6 0 0 1-6 6H6a6 6 0 0 1-6-6z"></path>
            <path fill="var(--icon-color, #101214)" d="M13.7 10.202h4.464c.67 0 .9.638.49 1.145l-6.98 8.615c-2.257-1.798-2.044-4.643-.393-6.719zm-3.449 3.433H5.788c-.67 0-.9-.637-.49-1.144l6.98-8.616c2.256 1.798 2.011 4.61.376 6.703z"></path>
        </svg>
    );
};

const SignInPromptPanel = (props: siteGrowthUnifiedProfileGetUnifiedProfileSitesProductType) => {
    const targetProduct = (productKey: string) => {
        switch (productKey) {
            case ProductKeys.JIRA_SOFTWARE:
                return {
                    productName: "Jira",
                    logo: <JiraLogo />}
                ;
            case ProductKeys.CONFLUENCE:
                return {
                    productName: "Confluence",
                    logo: <ConfluenceLogo />
                };
            case ProductKeys.JIRA_SERVICE_MANAGEMENT:
                return {
                    productName: "Jira Service Management",
                    logo: <JSMLogo />
                };
            default:
                return null;
        }
    };

    const productInfo = targetProduct(props.productKey);
    if (!productInfo) {
        return null;
    }

    const encodedContinueURI = encodeURIComponent(`${Environment.getAPIPrivateURL()}/start/promptSignin?productKey=${props.productKey}`);
    return (
        <a
            href={`${Environment.getIdentityBaseUrl()}/login?continue=${encodedContinueURI}`} 
            className="signin-prompt-panel-link" 
            data-event="clicked" 
            data-event-component="linkButton" 
            data-event-container="signinPromptDropdownMenu" 
            data-schema-version="1"
            onClick={() => {
                trackEvent("linkButton clicked", {
                    eventComponent: "linkButton",
                    action: "clicked",
                    component: "signinPromptDropdownMenu",
                    source: "globalNav",
                    actionSubjectId: "SigninPromptDropdownMenu-panel-link",
                    productName: props.productKey,
                });
            }}
        >
            <div className="signin-prompt-panel">
                <span className="signin-prompt-panel-logo">
                    {productInfo.logo}
                </span>
                <div className="signin-prompt-panel-header">
                    {productInfo.productName}
                </div>
            </div>
        </a>
    );
};

const SigninPromptDropdownMenu = ({
    welcomeBackText = "Welcome back.",
    pickupWhereYouLeftOffText = "Pick up where you left off.",
    lastVisitedAppsText = "Last visited apps",
    lastVisitedAppsTextSignular = "Last visited app",
    setIsOpenDropdown,
    displayProducts = [],
    setIsShowPulsingButton,
    setIsHovered
}: SigninPromptDropdownMenuProps) => {

    return (
        <SigninPromptDropdownMenuStyledDiv
            className="signin-prompt-dropdown-menu"
            data-testid="global-nav-signin-prompt-dropdown-menu"
        >
            <div className="signin-prompt-header-container">
                <div className="signin-prompt-header">
                    <div className="signin-prompt-header-text">
                        {welcomeBackText}
                        <br />
                        {pickupWhereYouLeftOffText}
                    </div>
                    <span className="signin-prompt-span-text">
                        {
                            displayProducts.length === 1
                                ? lastVisitedAppsTextSignular
                                : lastVisitedAppsText
                        }
                    </span>
                </div>
                <div className="closed-icon-container">
                    <button
                        className="close-icon"
                        tabIndex={0}
                        onClick={() => {
                            setIsShowPulsingButton(false);
                            setIsOpenDropdown(false);
                            setIsHovered(false);
                            trackEvent("linkButton clicked", {
                                eventComponent: "linkButton",
                                action: "clicked",
                                component: "signinPromptDropdownMenu",
                                source: "globalNav",
                                actionSubjectId: "SigninPromptDropdownMenu-closeIcon",
                            });
                        }}
                        aria-label="signin-prompt-modal-dismissal-button"
                    >
                        <CrossIcon label={"close"} size={"medium"} />
                    </button>
                </div>
            </div>            
            <div className="signin-prompt-panels-container">
                {displayProducts.map((product, index) => (
                    <SignInPromptPanel key={index} {...product} />
                ))}
            </div>
        </SigninPromptDropdownMenuStyledDiv>
    );
};


const SigninPromptIconAndDropdown = (props: { 
    domRootId?: string;
    displayProducts?: siteGrowthUnifiedProfileGetUnifiedProfileSitesProductType[];
    welcomeBackText: string;
    pickupWhereYouLeftOffText: string;
    lastVisitedAppsText: string;
    lastVisitedAppsTextSignular: string;
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isOpenDropdown, setIsOpenDropdown] = useState(false);
    const [isShowPulsingButton, setIsShowPulsingButton] = useState(false);
    const [isMenuStuck, setIsMenuStuck] = useState(false);

    const resizeGlobalNavLinkMenus = () => {
        const globalNav = document.querySelector("#adg4-global-nav") as HTMLElement;
        const genericNavLinks = globalNav.querySelectorAll(".nav-link") as NodeListOf<HTMLElement>;
        let wrappedElementIndex  = 0;
        let windowWidth = 0;

        if (document.documentElement.clientWidth > 1024 && !globalNav.classList.contains("menu-expanded")) {
          let hasWrapped = false;
          let firstWrapped = 0;
          const navLinksArray = Array.from(genericNavLinks);
          for (let i = 0; i < navLinksArray.length; i++) {
            const navLink = navLinksArray[i];
            if (navLink.offsetTop > 0 || (navLink.nextElementSibling && (navLink.nextElementSibling as HTMLElement).offsetTop > 0)) {
              navLink.classList.add("wrapped");
              if (!hasWrapped) {
                hasWrapped = true;
                firstWrapped = i;
              }
            } else if (
              (windowWidth > document.documentElement.clientWidth && wrappedElementIndex > i)
              ||
              (windowWidth < document.documentElement.clientWidth && wrappedElementIndex < i)
            ) {
              navLink.classList.remove("wrapped");
            }
          }
    
          wrappedElementIndex = firstWrapped;
          windowWidth = document.documentElement.clientWidth;
    
          if (hasWrapped) {
            globalNav.classList.add("menu-wrapped");
    
          } else {
            globalNav.classList.remove("menu-wrapped");
    
          }
        }
      };
    
    useEffect(() => {
        const handleScroll = () => {
            if (document.documentElement.clientWidth > 1024 && window.scrollY > 68 && document.querySelector(".subNav")) {
                setIsMenuStuck(true);
            } else {
                setIsMenuStuck(false);
            }
        };
        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);


    useEffect(() => {
        const localStorageItem="signin_prompt_first_visit";
        const hasVisitedBefore=window.localStorage.getItem(localStorageItem);
        if (!hasVisitedBefore) {
            setIsShowPulsingButton(true);
            window.localStorage.setItem(localStorageItem, new Date().toISOString());
            setIsHovered(true);
            setIsOpenDropdown(true);
        }
        resizeGlobalNavLinkMenus();
    }, []);


    useEffect(() => {
        if(isOpenDropdown && typeof ace !== "undefined") {
            ace.analytics.GASv3.sendUIEvent(
                'modal viewed',
                {
                    action: 'viewed',
                    event: 'viewed',
                    actionSubject: 'modal',
                    actionSubjectId: 'SigninPrompt-viewed',
                    eventComponent: 'modal',
                    component: "signinPromptDropdownMenu",
                    source: 'globalNav',
                    page_url: window.location.href,
                    page_path: window.location.pathname
                },
                false
            );
        }
    }, [isOpenDropdown]);

    return (
        <SigninPromptStyledContainer
            id="global-nav--wac__signin-prompt"
            data-testid="global-nav-signin-prompt-container"
        >
            <SigninPromptIcon
                isHovered={isHovered}
                isShowPulsingButton={isShowPulsingButton}
                isOpenDropdown={isOpenDropdown}
                setIsOpenDropdown={setIsOpenDropdown}
                setIsHovered={setIsHovered}
                setIsShowPulsingButton={setIsShowPulsingButton}
            />
            {isOpenDropdown && (
                <div
                    className={`signin-prompt-dropdown-menu-wrapper ${isMenuStuck ? "menu-stuck" : ""}`}
                >
                    <SigninPromptDropdownMenu
                        welcomeBackText={props.welcomeBackText}
                        pickupWhereYouLeftOffText={props.pickupWhereYouLeftOffText}
                        lastVisitedAppsText={props.lastVisitedAppsText}
                        lastVisitedAppsTextSignular={props.lastVisitedAppsTextSignular}
                        setIsOpenDropdown={setIsOpenDropdown}
                        displayProducts={props.displayProducts}
                        setIsShowPulsingButton={setIsShowPulsingButton}
                        setIsHovered={setIsHovered}
                    />
                </div>
            )}
        </SigninPromptStyledContainer>
    );
};

// ==================================================================
// End Sub components
// ==================================================================

const SigninPromptComponent = (props: {
    domRootId?: string;
    welcomeBackText: string;
    pickupWhereYouLeftOffText: string;
    lastVisitedAppsText: string;
    lastVisitedAppsTextSignular: string;
}) => {
    const [isDisplayDropdown, setIsDisplayDropdown] = useState(false);
    const [displayProducts, setDisplayProducts] = useState<siteGrowthUnifiedProfileGetUnifiedProfileSitesProductType[]>([])


    async function isLoadSigninPrompt() {
        let isPerformanceCookieEnabled = false;
        let ajs_anonymous_id_session = "";

        // check for performance cookie
        const oneTrustActiveGroups = storage.getCookie("OptanonConsent") || "";
        if (oneTrustActiveGroups) {
            const params = new URLSearchParams(oneTrustActiveGroups);
            const groups = params.get("groups");
            if (groups === null) {
                isPerformanceCookieEnabled = false;
            } else {
                const groupPairs = groups.split(",");
                isPerformanceCookieEnabled = groupPairs.includes("2:1");
            }
        }

        // Create session storage for gups call
        const sessionAnonymousId = sessionStorage.getItem("ajs_anonymous_id_session");
        if (sessionAnonymousId) {
            ajs_anonymous_id_session = sessionAnonymousId;
        } else {
            const anonymousId = storage.getCookie("ajs_anonymous_id") || "";
            if (anonymousId) {
                sessionStorage.setItem("ajs_anonymous_id_session", anonymousId);
                ajs_anonymous_id_session = anonymousId;
            }
        }

        // Feature gate check for testing and protect gups form production
        const isRunGupsCall = await imkt.utils.featureGate.checkGate("gwp-3944-signin-prompt-loggedout-gate");

        if (isPerformanceCookieEnabled && ajs_anonymous_id_session !== "" && isRunGupsCall) {
            try {
                const gupsProductData = await getUserProductsViaGUPS(ajs_anonymous_id_session);
                if (gupsProductData.length > 0) {
                    setDisplayProducts(gupsProductData);

                    const isJiraProductTourNavBarSolutionsTab = await imkt.utils.featureGate.getExperimentValue(
                        "gwp-3944-signin-prompt-loggedout-d1to6-eligible",
                        "variant",
                        false
                    );
                    if(isJiraProductTourNavBarSolutionsTab) {
                        setIsDisplayDropdown(true);
                    } else {
                        setIsDisplayDropdown(false);
                    }
                } else {
                    setIsDisplayDropdown(false);
                }
            } catch (error) {
                console.error("Error fetching gups data: ", error);
                setIsDisplayDropdown(false);
            }
        } else {
            setIsDisplayDropdown(false);
        }
    }

    useEffect(() => {
        if (storage.getCookie("login_user_detected") === "true") {
            setIsDisplayDropdown(false);
        } else {
            isLoadSigninPrompt();
        }
    }, []);

    return (
        isDisplayDropdown &&
            <SigninPromptIconAndDropdown 
                domRootId={props.domRootId} 
                displayProducts={displayProducts}
                welcomeBackText={props.welcomeBackText}
                pickupWhereYouLeftOffText={props.pickupWhereYouLeftOffText}
                lastVisitedAppsText={props.lastVisitedAppsText}
                lastVisitedAppsTextSignular={props.lastVisitedAppsTextSignular}
            />
    );
};

export default SigninPromptComponent;

